import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Download, BadgeCheck, User, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { companiesService } from "../../../services/companiesService";
import { catalogService } from "../../../services/catalogService";
import { CodeCard } from "../../../components/CodeCard";
import { delay, downloadBarcodePng, downloadQrPng } from "../../../utils/codeImages";
import type { Company, Product, Representative } from "../../../types";

export function ExhibitorQRPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const eventId = user?.assignedEventId ?? "";
  const companyId = user?.assignedCompanyId ?? "";

  const [company, setCompany] = useState<Company | undefined>();
  const [reps, setReps] = useState<Representative[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    if (!eventId || !companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [comp, repList, productList] = await Promise.all([
          companiesService.getById(companyId, eventId, "admin"),
          catalogService.listRepresentatives(eventId, companyId, "admin"),
          catalogService.listProducts(eventId, companyId, "admin"),
        ]);
        if (!cancelled) {
          setCompany(comp);
          setReps(repList);
          setProducts(productList);
        }
      } catch {
        if (!cancelled) {
          setCompany(undefined);
          setReps([]);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, companyId]);

  const handleDownloadAll = async () => {
    if (!company) return;
    setDownloadingAll(true);
    try {
      // Booth QR hidden for now — keep download path out of "Скачать все"
      for (const product of products) {
        if (!product.qrCode) continue;
        await downloadQrPng(product.qrCode, `${product.name}-product`);
        await delay(400);
      }
      for (const rep of reps) {
        if (rep.badgeQrCode) {
          await downloadQrPng(rep.badgeQrCode, `${rep.fullName}-badge-qr`);
          await delay(400);
        }
        if (rep.badgeBarcode) {
          downloadBarcodePng(rep.badgeBarcode, `${rep.fullName}-badge`);
          await delay(400);
        }
      }
      toast.success("Коды сохранены");
    } catch {
      toast.error("Не удалось скачать все коды");
    } finally {
      setDownloadingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!company) return <div className="p-6 text-gray-500">Компания не найдена</div>;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/exhibitor")}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hidden sm:block"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">QR-коды и штрихкоды</h1>
          <p className="text-[12px] text-gray-400">
            Скачайте, распечатайте и проверьте сканером QR-коды в приложении как посетитель
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleDownloadAll()}
          disabled={downloadingAll}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-[13px] hover:border-indigo-200 hover:text-indigo-600 transition-colors disabled:opacity-50"
        >
          <Download size={14} />
          {downloadingAll ? "Скачивание…" : "Скачать все"}
        </button>
      </div>

      <div className="bg-amber-50 rounded-xl p-4 text-[12px] text-amber-900 space-y-1 border border-amber-100">
        <p className="text-[13px] font-medium">Важно для проверки сканера</p>
        <p>
          Посетитель в приложении должен выбрать <strong>то же мероприятие</strong>, что и ваш
          стенд. Иначе код не будет найден.
        </p>
      </div>

      <div className="bg-gray-50 rounded-xl p-4 text-[12px] text-gray-600 space-y-2">
        <p className="text-[13px] text-gray-800">Как проверить:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Скачайте QR или штрихкод ниже</li>
          <li>Откройте раздел «Сканирование» — включится камера</li>
          <li>Выберите нужное мероприятие и наведите на QR-код</li>
        </ol>
      </div>

      {/* Booth QR section intentionally hidden — functionality kept for a later return */}

      {products.length > 0 && (
        <div>
          <h2 className="text-[13px] text-gray-500 uppercase tracking-wider mb-3">
            Товары · {products.length}
          </h2>
          <div className="space-y-3">
            {products.map((product) => (
              <CodeCard
                key={product.id}
                title={product.name}
                subtitle={[product.category, "QR для отдельного скана товара"]
                  .filter(Boolean)
                  .join(" · ")}
                qrValue={product.qrCode}
                badge="Товар"
                badgeColor="bg-violet-100 text-violet-700"
                downloadName={product.name}
              />
            ))}
          </div>
        </div>
      )}

      {reps.length > 0 && (
        <div>
          <h2 className="text-[13px] text-gray-500 uppercase tracking-wider mb-3">
            Бейджи · {reps.length} представителей
          </h2>
          <div className="space-y-3">
            {reps.map((rep) => (
              <CodeCard
                key={rep.id}
                title={rep.fullName}
                subtitle={[rep.position, "QR для личной карточки, штрихкод для бейджа"]
                  .filter(Boolean)
                  .join(" · ")}
                qrValue={rep.badgeQrCode}
                barcodeValue={rep.badgeBarcode}
                badge="Бейдж"
                badgeColor="bg-emerald-100 text-emerald-700"
                downloadName={rep.fullName}
              />
            ))}
          </div>
        </div>
      )}

      {reps.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 py-10 text-center">
          <User size={28} className="text-gray-200 mx-auto mb-2" />
          <p className="text-[13px] text-gray-400">Добавьте представителей чтобы получить бейджи</p>
          <button
            type="button"
            onClick={() => navigate("/exhibitor/reps")}
            className="mt-3 px-4 py-2 rounded-xl bg-indigo-50 text-indigo-600 text-[12px] hover:bg-indigo-100 transition-colors"
          >
            Управление представителями →
          </button>
        </div>
      )}

      {products.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 py-8 text-center">
          <Package size={24} className="text-gray-200 mx-auto mb-2" />
          <p className="text-[13px] text-gray-400">Добавьте товары — для каждого будет свой QR</p>
          <button
            type="button"
            onClick={() => navigate("/exhibitor/products")}
            className="mt-3 px-4 py-2 rounded-xl bg-violet-50 text-violet-600 text-[12px] hover:bg-violet-100 transition-colors"
          >
            Каталог товаров →
          </button>
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-gray-400">
        <BadgeCheck size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
        <span>
          На физическом бейдже обычно печатают штрихкод (1D). QR можно разместить на обороте или в приложении.
          Под каждым кодом показано его значение — у каждого объекта оно уникально.
        </span>
      </div>
    </div>
  );
}
